import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { SquareLocation } from '@/hooks/useSquareConnection';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locations: SquareLocation[];
  currentLocationId: string | null;
  onSelect: (locationId: string) => Promise<unknown>;
  isSaving: boolean;
}

export function SquareLocationPickerDialog({
  open,
  onOpenChange,
  locations,
  currentLocationId,
  onSelect,
  isSaving,
}: Props) {
  const [picked, setPicked] = useState<string | null>(currentLocationId);

  // Re-seed the selection each time the dialog opens so "Switch location"
  // starts from whatever is currently active.
  useEffect(() => {
    if (open) setPicked(currentLocationId);
  }, [open, currentLocationId]);

  const handleSave = async () => {
    if (!picked) {
      toast({ title: 'Pick a location first', variant: 'destructive' });
      return;
    }
    try {
      await onSelect(picked);
      toast({ title: 'Active Square location updated' });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Failed to set active location',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose your Square location</DialogTitle>
          <DialogDescription>
            Your Square account has more than one location. Pick the one that should receive
            invoice payments. It must settle in the same currency as your company.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {locations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No active locations were found on this Square account. Reconnect with an account
              that has at least one location with card processing enabled.
            </p>
          ) : (
            locations.map((loc) => {
              const isPicked = picked === loc.id;
              const isCurrent = currentLocationId === loc.id;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => setPicked(loc.id)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition',
                    isPicked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{loc.name || 'Unnamed location'}</div>
                      <div className="font-mono text-xs text-muted-foreground">ID {loc.id}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {loc.currency ?? 'Unknown currency'} · {loc.country ?? 'Unknown country'}
                      </div>
                    </div>
                    {isCurrent && <Badge variant="secondary">Current</Badge>}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !picked || picked === currentLocationId || locations.length === 0}
          >
            {isSaving ? 'Saving...' : 'Use this location'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
